/**
 * The explicit app-level state machine.
 *
 * Boot → Tutorial → Lobby → Room → Check → Match → Result → Rematch/Reset,
 * with Practice hanging off the lobby as a solo branch. The *match* phase
 * inside a room is owned by the server; this store only tracks which screen the
 * browser is showing and which loadout the player will carry in.
 */
import { create } from 'zustand';
import type { Spell } from '@spell/spell-schema';
import { loadProfile, saveProfile, clearProfile } from './persistence';

export type Screen =
  | 'boot'
  | 'tutorial'
  | 'lobby'
  | 'connecting'
  | 'check'
  | 'match'
  | 'practice'
  | 'result';

export interface FlowState {
  screen: Screen;
  name: string;
  tutorialDone: boolean;
  /** Built in the tutorial. Required before any room may be entered. */
  parrySpell: Spell | null;
  /** Built in the lobby. Required before a *duel* may be entered. */
  attackSpell: Spell | null;
  /** Last connection error shown on the lobby screen. */
  error: string | null;

  setScreen: (screen: Screen) => void;
  setName: (name: string) => void;
  completeTutorial: (parrySpell: Spell) => void;
  setParrySpell: (parrySpell: Spell) => void;
  setAttackSpell: (attackSpell: Spell) => void;
  setError: (error: string | null) => void;
  resetProfile: () => void;
  hydrate: () => void;
}

export const useFlow = create<FlowState>((set) => ({
  screen: 'boot',
  name: '',
  tutorialDone: false,
  parrySpell: null,
  attackSpell: null,
  error: null,

  setScreen: (screen) => set({ screen }),
  setName: (name) => {
    const trimmed = name.slice(0, 20);
    saveProfile({ name: trimmed });
    set({ name: trimmed });
  },
  completeTutorial: (parrySpell) => {
    saveProfile({ tutorialDone: true, parrySpell });
    set({ tutorialDone: true, parrySpell, screen: 'lobby' });
  },
  setParrySpell: (parrySpell) => {
    saveProfile({ parrySpell });
    set({ parrySpell });
  },
  setAttackSpell: (attackSpell) => {
    saveProfile({ attackSpell });
    set({ attackSpell });
  },
  setError: (error) => set({ error }),
  resetProfile: () => {
    clearProfile();
    set({ tutorialDone: false, parrySpell: null, attackSpell: null, screen: 'tutorial' });
  },
  hydrate: () => {
    const profile = loadProfile();
    set({
      name: profile.name || randomName(),
      tutorialDone: profile.tutorialDone,
      parrySpell: profile.parrySpell,
      attackSpell: profile.attackSpell,
      screen: profile.tutorialDone ? 'lobby' : 'tutorial',
    });
  },
}));

const NAME_PARTS = ['Ash', 'Vale', 'Corvin', 'Wren', 'Sable', 'Orin', 'Lyra', 'Fen', 'Roke', 'Isolde'];

function randomName(): string {
  const a = NAME_PARTS[Math.floor(Math.random() * NAME_PARTS.length)];
  return `${a}${Math.floor(Math.random() * 90 + 10)}`;
}
